import { cn } from '@/utils/tailwind-util';
import React, { forwardRef } from 'react';

interface Props extends Record<string, unknown> {
  iconElement: React.ReactElement;
  size?: 'xs' | 'xm' | 'sm' | 'md' | 'lg';
  iconSize?: 'xs' | 'xm' | 'sm' | 'md' | 'lg';
  color?: 'dark' | 'light' | 'primary' | 'white';
  hasPadding?: boolean;
  label?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  styles?: string;
}

const getButtonSizeStyle = (size: Props['size']) => {
  switch (size) {
    case 'xs':
      return 'p-1 max-h-[20px]';
    case 'xm':
      return 'p-1 max-h-[24px]';
    case 'sm':
      return 'p-1 max-h-[28px]';
    case 'md':
      return 'p-1.5 max-h-[36px]';
    case 'lg':
      return 'p-2.5 max-h-[48px]';
    default:
      return null;
  }
};

const getIconSizeStyle = (size: Props['size']) => {
  switch (size) {
    case 'xs':
      return 'w-3 h-3';
    case 'xm':
      return 'w-4 h-4';
    case 'sm':
      return 'w-5 h-5';
    case 'md':
      return 'w-6 h-6';
    case 'lg':
      return 'w-7 h-7';
    default:
      return null;
  }
};

const getIconColorStyle = (color: Props['color']) => {
  switch (color) {
    case 'dark':
      return 'text-gray-700 group-focus:text-gray-800';
    case 'light':
      return 'text-gray-400 group-focus:text-gray-500';
    case 'primary':
      return 'text-primary';
    case 'white':
      return 'text-white';
    default:
      throw new TypeError(
        `Expected color 'dark' or 'light', but received ${color}`
      );
  }
};

const IconButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      iconElement,
      size,
      iconSize,
      color,
      hasPadding,
      label,
      onClick,
      onKeyDown,
      styles,
      ...rest
    },
    ref
  ) => {
    return (
      <button
        type="button"
        ref={ref}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className={cn(
          'group',
          'flex items-center justify-center bg-transparent border border-transparent rounded focus:bg-gray-100 focus:border-gray-100 disabled:opacity-70 disabled:cursor-auto',
          styles,
          hasPadding ? getButtonSizeStyle(size) : 'p-0'
        )}
        {...rest}
      >
        <span className="sr-only">{label}</span>
        <span
          className={cn(
            'inline-block',
            getIconSizeStyle(iconSize || size),
            getIconColorStyle(color)
          )}
          aria-hidden="true"
        >
          {iconElement}
        </span>
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

IconButton.defaultProps = {
  size: 'md',
  color: 'dark',
  hasPadding: true,
  label: 'Label',
};

export default IconButton;
